import React, { useState, useMemo } from 'react';
import FilterIcon from './icons/FilterIcon';
import DollarIcon from './icons/DollarIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import BookmarkIcon from './icons/BookmarkIcon';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { USER_ROLES, OPPORTUNITY_TYPES } from '../types';
import type { Filters } from '../contexts/OpportunitiesProvider';

const instrumentOptions = ['Any Instrument', 'Guitar', 'Bass', 'Drums', 'Vocals', 'Keyboard', 'Violin', 'Saxophone', 'Other'];
const genreOptions = ['Any Genre', 'Rock', 'Pop', 'Jazz', 'Electronic', 'Hip-Hop', 'Classical', 'Folk', 'Other'];
const typeOptions = ['Any Type', ...OPPORTUNITY_TYPES];
const roleNeededOptions = ['Any Role', ...USER_ROLES];

interface FilterBarProps {
  filters: Filters;
  onFilterChange: (updater: (prev: Filters) => Filters) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { opportunities, saveCurrentFilters } = useOpportunities();

  // Extract unique locations for suggestions
  const availableCities = useMemo(() => {
    const citiesSet = new Set<string>();
    opportunities.forEach(opp => {
      if (opp.location && opp.status === 'open') {
        const city = opp.location.split(',')[0].trim();
        if (city) citiesSet.add(city);
      }
    });
    return Array.from(citiesSet).sort();
  }, [opportunities]);

  const availableRegions = useMemo(() => {
    const regionsSet = new Set<string>();
    opportunities.forEach(opp => {
      if (opp.region && opp.status === 'open') regionsSet.add(opp.region);
    });
    return Array.from(regionsSet).sort();
  }, [opportunities]);

  const availableCountries = useMemo(() => {
    const countriesSet = new Set<string>();
    opportunities.forEach(opp => {
      if (opp.country && opp.status === 'open') countriesSet.add(opp.country);
    });
    return Array.from(countriesSet).sort();
  }, [opportunities]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    onFilterChange(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    onFilterChange(prev => ({ ...prev, [name]: checked }));
  };
  
  const clearFilters = () => {
    onFilterChange(() => ({ 
        type: '',
        roleNeeded: '',
        genre: '',
        instrument: '',
        city: '',
        region: '',
        country: '',
        paidOnly: false,
        author: '', 
        minWage: '' 
    }));
  };

  const hasActiveFilters = !!(filters.type || filters.roleNeeded || filters.genre || filters.instrument || filters.city || filters.region || filters.country || filters.paidOnly);

  return (
    <div className="bg-brand-surface rounded-lg p-4 transition-all duration-300 border border-gray-800 shadow-sm font-urbanist">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
            <FilterIcon className="h-5 w-5 text-brand-orange" />
            <h3 className="text-lg font-black text-white uppercase tracking-tighter">Filter Feed</h3>
        </div>
        <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-4 mr-2">
                {hasActiveFilters && (
                    <>
                        <button 
                            onClick={(e) => { e.stopPropagation(); saveCurrentFilters(); }} 
                            className="text-[10px] font-black text-blue-400 hover:underline uppercase tracking-widest flex items-center gap-1"
                        >
                            <BookmarkIcon className="h-3 w-3" />
                            Save Filters
                        </button>
                        <button 
                            onClick={(e) => { e.stopPropagation(); clearFilters(); }} 
                            className="text-[10px] font-black text-brand-orange hover:underline uppercase tracking-widest"
                        >
                            Clear Filters
                        </button>
                    </>
                )}
            </div>
            <ChevronDownIcon className={`h-6 w-6 text-brand-muted transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[1000px] mt-4' : 'max-h-0'}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. Opportunity Type */}
            <div>
              <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">Opportunity Type</label>
              <div className="relative">
                <select name="type" value={filters.type} onChange={handleInputChange} className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-orange appearance-none font-bold">
                  {typeOptions.map(opt => <option key={opt} value={opt.startsWith('Any ') ? '' : opt}>{opt}</option>)}
                </select>
                <ChevronDownIcon className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" />
              </div>
            </div>

            {/* 2. Opportunity for */}
             <div>
              <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">Opportunity For</label>
              <div className="relative">
                <select name="roleNeeded" value={filters.roleNeeded} onChange={handleInputChange} className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-orange appearance-none font-bold">
                  {roleNeededOptions.map(opt => <option key={opt} value={opt.startsWith('Any ') ? '' : opt}>{opt}</option>)}
                </select>
                <ChevronDownIcon className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" />
              </div>
            </div>

            {/* 3. Genre */}
            <div>
              <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">Genre</label>
               <div className="relative">
                <select name="genre" value={filters.genre} onChange={handleInputChange} className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-orange appearance-none font-bold">
                  {genreOptions.map(opt => <option key={opt} value={opt.startsWith('Any ') ? '' : opt}>{opt}</option>)}
                </select>
                <ChevronDownIcon className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" />
              </div>
            </div>

            {/* 4. Instrument */}
            <div>
              <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">Instrument</label>
              <div className="relative">
                <select name="instrument" value={filters.instrument} onChange={handleInputChange} className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-orange appearance-none font-bold">
                  {instrumentOptions.map(opt => <option key={opt} value={opt.startsWith('Any ') ? '' : opt}>{opt}</option>)}
                </select>
                <ChevronDownIcon className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" />
              </div>
            </div>

            {/* 5. City */}
            <div>
              <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">City</label>
              <div className="relative">
                <input
                  type="text"
                  name="city"
                  list="suggested-cities"
                  value={filters.city}
                  onChange={handleInputChange}
                  placeholder="e.g., Milan"
                  className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-orange font-bold"
                />
                <datalist id="suggested-cities">
                    {availableCities.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
            </div>

            {/* 6. Region */}
            <div>
              <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">Region</label>
              <div className="relative">
                <input
                  type="text"
                  name="region"
                  list="suggested-regions"
                  value={filters.region}
                  onChange={handleInputChange}
                  placeholder="e.g., Lombardia"
                  className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-orange font-bold"
                />
                <datalist id="suggested-regions">
                    {availableRegions.map(r => <option key={r} value={r} />)}
                </datalist>
              </div>
            </div>

            {/* 7. Country */}
            <div>
              <label className="block text-[10px] font-black text-brand-muted mb-1 uppercase tracking-widest">Country</label>
              <div className="relative">
                <input
                  type="text"
                  name="country"
                  list="suggested-countries"
                  value={filters.country}
                  onChange={handleInputChange}
                  placeholder="e.g., Italy"
                  className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-orange font-bold"
                />
                <datalist id="suggested-countries">
                    {availableCountries.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
            </div>

            {/* 8. Paid Only */}
            <div className="flex items-end">
                <label className={`flex items-center justify-center w-full h-10 px-4 rounded-md text-sm cursor-pointer transition-all border font-bold ${filters.paidOnly ? 'bg-brand-orange/10 border-brand-orange text-white' : 'bg-gray-700 border-gray-600 text-brand-muted hover:bg-gray-600'}`}>
                    <DollarIcon className={`h-4 w-4 mr-2 ${filters.paidOnly ? 'text-brand-orange' : 'text-green-400'}`} />
                    <span className="flex-grow uppercase tracking-widest text-[10px]">Paid Only</span>
                    <input
                        type="checkbox"
                        name="paidOnly"
                        checked={filters.paidOnly}
                        onChange={handleCheckboxChange}
                        className="h-4 w-4 rounded bg-gray-800 border-gray-600 text-brand-orange focus:ring-brand-orange ml-2"
                    />
                </label>
            </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;